import React from 'react';
import { useTranslation } from 'react-i18next';
import AllDayToggle from '../planning/AllDayToggle';
import useEventCategories from '../planning/useEventCategories';

export default function EditEventPopover({
  open,
  style,
  whenLabel,
  dayIso,
  endDayIso,
  startTime,
  endTime,
  allDay,
  title,
  category,
  timeError,
  onChange,
  onClose,
  onSubmit,
  onDelete,
}) {
  const { t } = useTranslation('planning');
  const { t: tCommon } = useTranslation('common');
  const { codes, labels } = useEventCategories();
  if (!open) return null;
  return (
    <div className="fixed z-[70]" style={style}>
      <div className="w-80 bg-surface p-4 rounded-xl shadow-[0_8px_24px_rgba(18,65,145,0.18)] border border-border" role="dialog">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-1.5 h-5 rounded-full bg-nokia-blue shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-bold text-primary">{t('editEvent')}</h2>
              <button
                type="button"
                className="p-0.5 text-text-secondary hover:text-primary rounded transition-colors"
                aria-label={tCommon('close')}
                onClick={onClose}
              >
                <span className="material-symbols-outlined text-[18px]">close</span>
              </button>
            </div>
            <p className="text-xs text-text-secondary mt-0.5">{whenLabel}</p>
          </div>
        </div>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit?.();
          }}
        >
          <div>
            <label htmlFor="wv-edit-title" className="block text-[11px] font-semibold text-text-secondary mb-1">
              {t('eventTitleOptional')} <span className="font-normal opacity-70">{t('optionalSuffix')}</span>
            </label>
            <input
              id="wv-edit-title"
              type="text"
              maxLength={80}
              placeholder={t('addTitle')}
              value={title}
              onChange={(e) => onChange?.({ title: e.target.value })}
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-main placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-nokia-blue/20 focus:border-nokia-blue transition-all"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="wv-edit-day" className="block text-[11px] font-semibold text-text-secondary mb-1">
                {t('startDay')}
              </label>
              <input
                id="wv-edit-day"
                type="date"
                required
                value={dayIso || ''}
                onChange={(e) => onChange?.({ dayIso: e.target.value })}
                className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-main focus:outline-none focus:ring-2 focus:ring-nokia-blue/20 focus:border-nokia-blue transition-all"
              />
            </div>
            <div>
              <label htmlFor="wv-edit-end-day" className="block text-[11px] font-semibold text-text-secondary mb-1">
                {t('endDay')}
              </label>
              <input
                id="wv-edit-end-day"
                type="date"
                required
                value={endDayIso || dayIso || ''}
                min={dayIso || undefined}
                onChange={(e) => onChange?.({ endDayIso: e.target.value })}
                className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-main focus:outline-none focus:ring-2 focus:ring-nokia-blue/20 focus:border-nokia-blue transition-all"
              />
            </div>
          </div>
          <AllDayToggle
            id="wv-edit-all-day"
            checked={!!allDay}
            onChange={(checked) => onChange?.({ allDay: checked })}
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="wv-edit-start" className="block text-[11px] font-semibold text-text-secondary mb-1">
                {t('startTime')}
              </label>
              <input
                id="wv-edit-start"
                type="time"
                required
                disabled={!!allDay}
                value={allDay ? '00:00' : startTime || '09:00'}
                onChange={(e) => onChange?.({ startTime: e.target.value })}
                className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-main focus:outline-none focus:ring-2 focus:ring-nokia-blue/20 focus:border-nokia-blue transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </div>
            <div>
              <label htmlFor="wv-edit-end" className="block text-[11px] font-semibold text-text-secondary mb-1">
                {t('endTime')}
              </label>
              <input
                id="wv-edit-end"
                type="time"
                required
                disabled={!!allDay}
                value={allDay ? '23:59' : endTime || '10:00'}
                onChange={(e) => onChange?.({ endTime: e.target.value })}
                className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-main focus:outline-none focus:ring-2 focus:ring-nokia-blue/20 focus:border-nokia-blue transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </div>
          </div>
          {timeError ? (
            <p className="text-[11px] text-red-600 font-medium">{t('endMustBeAfterStart')}</p>
          ) : null}
          <div>
            <span className="block text-[11px] font-semibold text-text-secondary mb-1.5">{t('category')}</span>
            <div className="category-radio-grid" role="radiogroup" aria-label={t('eventCategory')}>
              {codes.map((cat) => (
                <label key={cat} className="flex flex-col items-center justify-center">
                  <input
                    type="radio"
                    name="wv-edit-category"
                    value={cat}
                    checked={category === cat}
                    onChange={() => onChange?.({ category: cat })}
                  />
                  <span className="text-center w-full">{labels[cat] || cat}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-main hover:bg-background-light rounded-lg transition-colors"
            >
              {tCommon('cancel')}
            </button>
            <button
              type="submit"
              className="px-3 py-1.5 bg-primary hover:bg-primary-hover text-white text-xs font-medium rounded-lg active:scale-[0.98] transition-all"
            >
              {tCommon('save')}
            </button>
          </div>
        </form>
      </div>
      <button
        type="button"
        onClick={onDelete}
        className="mt-2 w-80 flex items-center justify-center gap-2 px-3 py-2.5 bg-surface border border-red-200 dark:border-red-800/60 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 hover:border-red-300 dark:hover:border-red-700 rounded-xl text-xs font-semibold shadow-[0_8px_24px_rgba(18,65,145,0.12)] dark:shadow-[0_8px_24px_rgba(0,0,0,0.4)] transition-colors"
      >
        <span className="material-symbols-outlined text-[18px]">delete</span>
        {t('deleteEvent')}
      </button>
    </div>
  );
}
